import { MouseEvent, useCallback } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';

import { loggedSelector } from '@lootboy/lib/selectors';

import { useAnimateElement } from '~/hooks/useAnimateElement';
import useBooleanState from '~/hooks/useBooleanState';
import usePrevious from '~/hooks/usePrevious';

import useLoginFlow from './useLoginFlow';

import styles from './WelcomeScreen.module.scss';

export default function WelcomeScreenWrapper(): JSX.Element | null {
  const isLoggedIn = useSelector(loggedSelector);
  const previousIsLoggedIn = usePrevious(isLoggedIn);

  if (isLoggedIn) {
    return <WelcomeScreen previousIsLoggedIn={previousIsLoggedIn} />;
  }
  return null;
}

function WelcomeScreen({
  previousIsLoggedIn,
}: {
  previousIsLoggedIn: boolean;
}): JSX.Element | null {
  const [wasShown, { setTrue: setWasShownTrue }] = useBooleanState(false);
  const { ref, animate } = useAnimateElement<HTMLDivElement>({
    options: {
      duration: 250,
      iterations: 1,
      easing: 'ease-in-out',
      fill: 'forwards',
    },
    animationKeyframes: {
      opacity: [1, 0],
    },
  });

  const close = useCallback(
    (event?: MouseEvent<HTMLDivElement>) => {
      event?.stopPropagation();
      if (!wasShown) {
        animate().then(setWasShownTrue);
      }
    },
    [wasShown, animate, setWasShownTrue],
  );

  const message = useLoginFlow(close, previousIsLoggedIn);

  if (!message || wasShown) {
    return null;
  }

  return (
    <div
      className={styles.container}
      onClick={close}
    >
      <div
        className={styles.background}
        ref={ref}
      >
        <h2 className={styles.headline}>{I18n.t(message)}</h2>
      </div>
    </div>
  );
}
